import { type SessionV2 } from "@decentriq/core";
import type JSZip from "jszip";
import { type SnackbarKey } from "notistack";
import { useCallback, useEffect, useState } from "react";
import {
  MediaDataRoomJobInput,
  useMediaDataRoomJob,
} from "features/mediaDataRoom/hooks/v3";
import {
  type OverlapSegmentsCollection,
  type PublishedDatasetsHashes,
} from "features/mediaDataRoom/models";
import { mapMediaDataRoomErrorToSnackbar, useDataRoomSnackbar } from "hooks";
import { type InsightsData, type InsightsResult } from "../models";
import { filterNullableOverlapInsightsRows } from "../utils";

interface InsightsHookPayload {
  dataRoomId: string;
  publishedDatasetsHashes: PublishedDatasetsHashes;
  session?: SessionV2;
  skip: boolean;
}

export type InsightsHookResult = InsightsResult;

const useInsights = ({
  dataRoomId,
  publishedDatasetsHashes,
  session,
  skip,
}: InsightsHookPayload): InsightsHookResult => {
  const { enqueueSnackbar, closeSnackbar } = useDataRoomSnackbar();
  const setErrorSnackbarId = useState<SnackbarKey | undefined>()[1];
  const transform = useCallback(async (zip: JSZip): Promise<InsightsData> => {
    const overlapInsightsSegmentsFile = zip.file("segments.json");
    if (overlapInsightsSegmentsFile === null) {
      throw new Error("segments.json not found in zip");
    }
    const overlapInsightsSegments = JSON.parse(
      await overlapInsightsSegmentsFile.async("string")
    ) as OverlapSegmentsCollection;
    const insights = filterNullableOverlapInsightsRows(overlapInsightsSegments);
    return {
      insights,
      segmentsRawFile: overlapInsightsSegmentsFile,
    };
  }, []);
  const computeJob = useMediaDataRoomJob({
    input: MediaDataRoomJobInput.create(
      "computeInsights",
      dataRoomId,
      publishedDatasetsHashes
    ),
    requestCreator: MediaDataRoomJobInput.emptyRequestCreator,
    session,
    skip,
    transform,
  });
  useEffect(() => {
    if (computeJob.error) {
      const snackbarId = enqueueSnackbar(
        ...mapMediaDataRoomErrorToSnackbar(
          computeJob.error,
          `Cannot fetch insights`
        )
      );
      setErrorSnackbarId(snackbarId);
    } else {
      setErrorSnackbarId((snackbarId) => {
        if (snackbarId) {
          closeSnackbar(snackbarId);
        }
        return undefined;
      });
    }
  }, [enqueueSnackbar, closeSnackbar, setErrorSnackbarId, computeJob]);
  return computeJob;
};

export default useInsights;
